import { actionCreator, asyncActionCreators } from "../../lib/util/ReduxUtils";
import * as Api from "../../Api";
import * as UserSelectors from "../../store/User/User.selector";
import * as Utils from "../../lib/util/Utils";
import * as NavigationUtils from "../../lib/util/NavigationUtils";
import { Invite, User } from "../../lib/types/UserTypes";
import { DriverSort } from "./DriverIndex";
import { FilterElement } from "../../lib/types/FilterSortTypes";

export const setFilterSelection = actionCreator<string | null>(
	"[DRIVER INDEX] SET_FILTER_SELECTION",
	(action, dispatch) => {
		const newHash = action.payload || "all";

		Utils.nextFrame(() => {
			if (NavigationUtils.getHash() !== newHash) {
				NavigationUtils.setHash(newHash);
			}
		});

		if (newHash === "invited") {
			dispatch(setSortSelection([["lastInviteSent"], "desc"]));
		}
	},
);

export const setSortSelection = actionCreator<DriverSort>(
	"[DRIVER INDEX] SET_SORT_SELECTION",
);

export const toggleInviteModal = actionCreator<boolean>(
	"[DRIVER INDEX] TOGGLE_INVITE_MODAL",
);

export const remindDriver = asyncActionCreators<
	{
		driver: any;
		subject?: string;
		message?: string;
	},
	any
>("[DRIVER INDEX] REMIND_DRIVER", async (action, dispatch, getState) => {
	const { driver, subject, message } = action.payload;

	const result = await Api.postSendReminder.submit({
		query: { group: driver.group },
		params: { fleetId: driver.fleetId },
		body: {
			email: driver.email,
			subject,
			message,
		},
	});

	return result.data;
});

export const messageInactiveDriver = asyncActionCreators<
	{
		driver: User;
		subject?: string;
		message?: string;
	},
	any
>("[DRIVER INDEX] MESSAGE_INACTIVE_DRIVER", async (action, dispatch) => {
	const { driver, subject, message } = action.payload;

	const result = await Api.postSendInactiveMessage.submit({
		query: { group: driver.group },
		params: { fleetId: driver.fleetId },
		body: {
			email: driver.email,
			subject,
			message,
		},
	});

	return result.data;
});

export const setDriverContacted = asyncActionCreators<any, any>(
	"[DRIVER INDEX] SET_DRIVER_CONTACTED",
	async (action, dispatch) => {
		const { driverDetails, status } = action.payload;

		const result = await Api.postSetDriverContacted.submit({
			query: { group: driverDetails.group },
			params: { fleetId: driverDetails.fleetId },
			body: { email: driverDetails.email, status: status.id },
		});

		return result.data;
	},
);

export const bulkRemind = asyncActionCreators<
	{
		filter: FilterElement;
		fleetId: string;
		group?: string;
		excludeRecent: boolean;
		onSuccess: () => void;
		subject?: string;
		message?: string;
	},
	any
>("[DRIVER INDEX] BULK_REMIND", async (action, dispatch, getState) => {
	const { excludeRecent, subject, message, filter, fleetId, group, onSuccess } =
		action.payload;
	const res = await Api.postSendReminderBulk.submit({
		params: { fleetId: fleetId },
		query: { group },
		body: {
			filter,
			subject,
			message,
			excludeRecent: excludeRecent,
		},

		options: {},
	});

	onSuccess();
	return {
		drivers: /*--DRIVERS--*/ [],
		remindersSent: res.data.length,
	};
});

export const bulkMessageInactiveDrivers = asyncActionCreators<
	{
		filter: FilterElement;
		fleetId: string;
		group?: string;
		excludeRecent: boolean;
		onSuccess: () => void;
		subject?: string;
		message?: string;
	},
	any
>(
	"[DRIVER INDEX] MESSAGE_INACTIVE_DRIVER",
	async (action, dispatch, getState) => {
		const {
			excludeRecent,
			subject,
			message,
			filter,
			fleetId,
			group,
			onSuccess,
		} = action.payload;

		const result = await Api.postSendBulkInactiveMessage.submit({
			params: { fleetId: fleetId },
			query: { group },
			body: {
				filter,
				subject,
				message,
				excludeRecent: excludeRecent,
			},
		});

		onSuccess();

		return result.data;
	},
);

export const updateInvite = asyncActionCreators<
	{ key: string; fleetId: string; group?: string; data: Partial<Invite> },
	Invite
>("[DRIVER INDEX] UPDATE_INVITE", async (action, dispatch) => {
	const { fleetId, key, data, group } = action.payload;

	const res = await Api.postInviteEdit.submit({
		query: { group },
		params: { fleetId: fleetId },
		body: { inviteId: key, data },
	});

	return res.data;
});

export const removeInvite = asyncActionCreators<
	{ fleetId: string; inviteId: string; group?: string },
	User[]
>("[DRIVER INDEX] REMOVE_INVITE", async (action, dispatch, getState) => {
	const { fleetId, inviteId, group } = action.payload;

	await Api.postInviteCancel.submit({
		params: { fleetId: fleetId },
		query: { group },
		body: { inviteId },
	});

	return [];
});

export const retryInvite = asyncActionCreators<string, any>(
	"[DRIVER INDEX] RETRY_INVITE",
	async (action, dispatch, getState) => {
		const state = getState();
		const fleetId = UserSelectors.selectCurrentScopeFleetId(state);
		const group = UserSelectors.selectCurrentScopeGroupId(state);
		if (!fleetId) {
			throw new Error("Missing fleet ID");
		}
		const email = action.payload;

		const res = await Api.postInviteRetry.submit({
			query: { group },
			params: { fleetId: fleetId },
			body: { email },
		});

		return res.data;
	},
);
